import { ActionReducer } from '@ngrx/store';
import { SESSION_RESET } from '../session/session.actions';
import { sharedStateKey } from '../index';

type RootState = Record<string, unknown>;

/**
 * Root meta-reducer that drops every feature slice on a session change, so
 * nothing belonging to one account survives into the next.
 *
 * Two ordering facts make this work, and both are load-bearing:
 *
 * 1. Root meta-reducers wrap combineReducers, which puts this OUTSIDE every
 *    feature's localStorage sync meta-reducer. Each feature's sync pass then
 *    sees the already-cleared slice and, with removeOnUndefined, rewrites its
 *    own keys in the same synchronous dispatch. No localStorage.clear(), no
 *    timer, no race.
 * 2. ngrx-store-localstorage only re-merges its rehydrated snapshot for
 *    @ngrx/store/init and @ngrx/store/update-reducers. A session reset is
 *    neither, so clearing here is not immediately undone. The sync helper
 *    additionally invalidates that snapshot so a later lazy provideState
 *    cannot resurrect it.
 */
export function resetStateOnSessionChange(reducer: ActionReducer<RootState>): ActionReducer<RootState> {
  return (state, action) => {
    if (action.type !== SESSION_RESET) {
      return reducer(state, action);
    }

    // The theme is a device preference, not session data. Carry it through the
    // reset rather than patching it back afterwards, so the shared slice's own
    // sync pass persists the preserved value and store and disk agree.
    const themeState = (state?.[sharedStateKey] as { themeState?: unknown } | undefined)?.themeState;
    const carried = themeState === undefined ? undefined : ({ [sharedStateKey]: { themeState } } as RootState);

    return reducer(carried, action);
  };
}
